import { useId, type ReactNode } from 'react';
import { cn } from '../cn.js';

export interface FieldProps {
  label: ReactNode;
  /** Rendered next to the label — "(optional)", a unit hint. Never required for comprehension. */
  hint?: ReactNode;
  /** Validation message. Presence alone marks the field invalid — passed through to the control via
   *  `aria-invalid`/`aria-describedby` by whoever renders `htmlFor`. */
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
  /** Id given to the wrapped control. Generated when omitted; pass your own to keep a stable id
   *  across renders (e.g. when the control is controlled elsewhere). */
  id?: string;
}

/**
 * Label + control + error/hint, laid out the same way everywhere a form asks a question. Does not
 * clone its child: pass the same string to both `Field`'s `id` and the wrapped `Input`/`Textarea`/
 * `Select`'s `id` so the `<label htmlFor>` pairing (and screen-reader association) holds. Omit `id`
 * on `Field` and it generates one — but then the control itself doesn't get it automatically, so an
 * explicit shared `id` is the reliable pairing when the label association matters.
 *
 * Named for what it holds — a form FIELD — not for its layout ("StackedLabel").
 */
export function Field({ label, hint, error, required, className, children, id }: FieldProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const errorId = `${controlId}-error`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={controlId} className="text-sm font-medium text-fg">
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {hint && <span className="text-xs text-fg-subtle">{hint}</span>}
      </div>
      {/* Cloning is deliberately avoided — Input/Textarea/Select accept `id` directly, so callers
          just pass field.id (this component's return value doesn't expose one; instead the
          convention is: give the control the SAME id string you pass to Field). */}
      {children}
      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
